import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RptSumReviewRow,
  getRptSumRows,
  getRptTargets,
  saveRptSumReview,
  saveRptSumTargetYn,
} from 'apis/ut/UtMatrixComparisonReportList';
import { useMessageBar } from 'hooks/useMessageBar';
import useSessionStore from 'stores/useSessionStore';
import { CommonUtil } from 'utils/CommonUtil';
import { canExecuteDml, errorMessage } from '../elec/api';
import { AtcDivsCdSum, MstTgtTpCd, ReportFormat, RptSumItem, RptUtmTarget } from '../types';
import { exportUtMatrixRptPdf } from './exportPdf';
import { exportUtMatrixRptPpt } from './exportPpt';
import { rptSumDiff } from './rptSumDiff';
import { AnalysisReportRoot } from './styles';
import {
  EMPTY_UT_ITEM_CODES,
  fetchUtItemCodes,
  isSameUtItemCode,
  UtItemCode,
  UtItemCodes,
} from './utItemCodes';

const ROLE_LABEL: Record<MstTgtTpCd, string> = { MST: '기준', TGT: '비교대상', REF: '참조' };
/** 표에 그리는 순서 — 참조, 기준, 비교대상 (ref 구현과 동일) */
const ROLE_ORDER: MstTgtTpCd[] = ['REF', 'MST', 'TGT'];

type ReviewDraft = { infcRvwCtn: string; rvwCtn: string; nextPlanCtn: string };
const EMPTY_DRAFT: ReviewDraft = { infcRvwCtn: '', rvwCtn: '', nextPlanCtn: '' };

/** 항목 식별키 — 구분(전기/기계) + UT 항목코드 */
const draftKey = (item: RptSumItem) => `${item.atcDivsCd}|${item.utmTpCd}`;

/**
 * 표(용량 정합화 헤더 / 검토 내역)에 그릴 항목 — **구성과 순서는 공통코드가 정한다.**
 *
 * 값(용량·부족량·검토 입력)은 SUM_M에만 있으므로 저장된 행에서 가져오고, 공통코드에 없는
 * 행은 그리지 않는다. 필터 버튼과 같은 규칙이라 버튼에 없는 항목이 표에만 남는 일이 없다.
 */
const visibleItemsOf = (codes: UtItemCode[], rows: RptSumItem[], isMech: boolean): RptSumItem[] =>
  codes
    .map((c) => rows.find((i) => isSameUtItemCode(c.cd, i.utmTpCd, isMech)))
    .filter((i): i is RptSumItem => !!i && i.rpotTgtYn === 'Y');

/**
 * 기계 항목은 이름 뒤에 단위를 붙인다 — `CDA [Nl/min]`.
 * 전기는 전부 MW라 표의 그룹 헤더(`전기(MW)`)로 충분해서 붙이지 않는다.
 */
const withUnit = (nm: string, unit: string, isMech: boolean): string =>
  isMech && unit ? `${nm} [${unit}]` : nm;

const fmt = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : v.toLocaleString('ko-KR', { maximumFractionDigits: 1 });

interface Props {
  rptId: string;
  /** 보고서 발행 파일명에 쓰는 분석명 */
  rptNm?: string;
}

/**
 * "분석 Report" 탭 — TB_EELMB_UTM_RPT_SUM_M 기반.
 *
 * ① 대상 UT Matrix 뱃지, ② UT Matrix 용량 정합화(전기+기계 통합 표, 필터 버튼으로
 * 표시/숨김을 DB의 RPOT_TGT_YN에 그대로 반영), ③ UT 사용량 정합화 검토 내역
 * (부족량 + 투자비영향/내역/향후계획 입력), ④ 표시 중인 항목만 담은 PPT/PDF 발행.
 *
 * 화면 원본은 ref/html/ut_matrix_rpt_demo.html의 "분석 Report" 탭이고,
 * 구현은 ref/frontend/features/UtMatrixRpt/components/AnalysisReportPanel.tsx를
 * 이 프로젝트 규약(useSessionStore / apis/ut / useMessageBar)에 맞춰 옮긴 것이다.
 *
 * ⚠ SUM_M 조회/저장 4개 엔드포인트는 아직 BE 미개발이다(ref/BE-REQUEST-rpt-sum.md).
 *   호출이 실패하면 표 대신 안내문구를 보여주고 나머지 탭에는 영향을 주지 않는다.
 */
const AnalysisReportPanel = ({ rptId, rptNm = '' }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const contentRef = useRef<HTMLDivElement>(null);
  const userId = useSessionStore((s) => s.userId);
  const canSave = canExecuteDml();

  const [format, setFormat] = useState<ReportFormat>('PPT');
  const [targets, setTargets] = useState<RptUtmTarget[]>([]);
  const [items, setItems] = useState<RptSumItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState<Record<string, ReviewDraft>>({});
  /** UT 항목 목록의 기준 — 리포트 데이터가 아니라 공통코드에서 온다 */
  const [itemCodes, setItemCodes] = useState<UtItemCodes>(EMPTY_UT_ITEM_CODES);
  const [savingAll, setSavingAll] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // 공통코드는 리포트와 무관한 마스터라 최초 1회만 읽는다
  useEffect(() => {
    let alive = true;
    fetchUtItemCodes().then((codes) => {
      if (alive) setItemCodes(codes);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!rptId) {
      setTargets([]);
      setItems(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setError('');
    Promise.all([getRptTargets(rptId), getRptSumRows(rptId)])
      .then(([targetRows, sumRows]) => {
        if (!alive) return;
        setTargets(targetRows);
        setItems(sumRows);
        const next: Record<string, ReviewDraft> = {};
        sumRows.forEach((r) => {
          next[draftKey(r)] = {
            infcRvwCtn: r.infcRvwCtn,
            rvwCtn: r.rvwCtn,
            nextPlanCtn: r.nextPlanCtn,
          };
        });
        setDrafts(next);
      })
      .catch((e) => alive && setError(errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [rptId]);

  const orderedTargets = useMemo(
    () =>
      ROLE_ORDER.map((role) => targets.find((x) => x.role === role)).filter(
        (x): x is RptUtmTarget => !!x
      ),
    [targets]
  );
  const tgtUtmId = orderedTargets.find((x) => x.role === 'TGT')?.utmId ?? '';

  const elecItems = useMemo(() => (items ?? []).filter((i) => i.atcDivsCd === 'ELEC'), [items]);
  const mechItems = useMemo(() => (items ?? []).filter((i) => i.atcDivsCd === 'MECH'), [items]);
  const visibleElec = visibleItemsOf(itemCodes.elec, elecItems, false);
  const visibleMech = visibleItemsOf(itemCodes.mech, mechItems, true);
  const reviewItems = [...visibleElec, ...visibleMech];

  /** 저장된 행 ↔ 공통코드 대응 — 없으면 undefined (아직 데이터가 없는 항목) */
  const codeOf = (item: RptSumItem): UtItemCode | undefined => {
    const isMech = item.atcDivsCd === 'MECH';
    const list = isMech ? itemCodes.mech : itemCodes.elec;
    return list.find((c) => isSameUtItemCode(c.cd, item.utmTpCd, isMech));
  };

  /** 표시명·단위는 공통코드를 우선한다 (공통코드 미등록이면 저장된 값으로 degrade) */
  const itemLabel = (item: RptSumItem) => codeOf(item)?.nm || item.utmTpNm;
  const itemUnit = (item: RptSumItem) => codeOf(item)?.unit || item.msrUnitCd;

  /** 항목 하나의 표시여부 토글 — 기준/비교대상/참조 행에 함께 반영된다 */
  const toggleItem = async (item: RptSumItem) => {
    if (!rptId || !canSave) return;
    const nextYn = item.rpotTgtYn === 'Y' ? 'N' : 'Y';
    try {
      await saveRptSumTargetYn(rptId, item.atcDivsCd, item.utmTpCd, nextYn, userId);
      setItems(
        (prev) =>
          prev?.map((i) =>
            i.atcDivsCd === item.atcDivsCd && i.utmTpCd === item.utmTpCd
              ? { ...i, rpotTgtYn: nextYn }
              : i
          ) ?? null
      );
    } catch (e) {
      openMessageBar({ type: 'error', content: `표시 설정 변경 오류: ${errorMessage(e)}` });
    }
  };

  /** 숨겨둔 항목을 한 번에 되돌리는 "전체" 버튼 */
  const showAll = async (atcDivsCd: AtcDivsCdSum) => {
    if (!rptId || !canSave) return;
    const hidden = (items ?? []).filter((i) => i.atcDivsCd === atcDivsCd && i.rpotTgtYn === 'N');
    if (hidden.length === 0) return;
    try {
      await Promise.all(
        hidden.map((i) => saveRptSumTargetYn(rptId, atcDivsCd, i.utmTpCd, 'Y', userId))
      );
      setItems(
        (prev) =>
          prev?.map((i) => (i.atcDivsCd === atcDivsCd ? { ...i, rpotTgtYn: 'Y' } : i)) ?? null
      );
    } catch (e) {
      openMessageBar({ type: 'error', content: `표시 설정 변경 오류: ${errorMessage(e)}` });
    }
  };

  const setDraftField = (item: RptSumItem, field: keyof ReviewDraft, value: string) => {
    const key = draftKey(item);
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? EMPTY_DRAFT), [field]: value },
    }));
  };

  /** 행별 저장은 없다 — 표시 중인 항목 전체를 한 번에 보낸다 */
  const saveAllReviews = async () => {
    if (!rptId || !tgtUtmId || reviewItems.length === 0) return;
    setSavingAll(true);
    try {
      const rows: RptSumReviewRow[] = reviewItems.map((item) => ({
        atcDivsCd: item.atcDivsCd,
        utmTpCd: item.utmTpCd,
        ...(drafts[draftKey(item)] ?? EMPTY_DRAFT),
      }));
      await saveRptSumReview(rptId, tgtUtmId, rows, userId);
      setItems(
        (prev) =>
          prev?.map((i) => {
            const draft = drafts[draftKey(i)];
            return draft ? { ...i, ...draft } : i;
          }) ?? null
      );
      openMessageBar({ type: 'confirm', content: t('com.msg.저장되었습니다.', '저장되었습니다.') });
    } catch (e) {
      openMessageBar({ type: 'error', content: `검토 내역 저장 오류: ${errorMessage(e)}` });
    } finally {
      setSavingAll(false);
    }
  };

  const handlePublish = async () => {
    if (orderedTargets.length === 0 || reviewItems.length === 0) return;
    setPublishing(true);
    try {
      if (format === 'PDF') {
        const panel = contentRef.current;
        if (!panel) return;
        /*
          PDF는 이 패널을 html2canvas로 캡쳐한다. 용량 정합화 표는 항목이 많으면 가로
          스크롤이 생기는데(styles.ts의 .sum-table), 스크롤 컨테이너는 **보이는 부분만**
          찍혀 오른쪽 항목이 잘린다. 캡쳐 동안만 스크롤을 풀어 표를 전부 펼쳐둔다.
        */
        panel.classList.add('pdf-capturing');
        try {
          await exportUtMatrixRptPdf(rptNm, panel);
        } finally {
          panel.classList.remove('pdf-capturing');
        }
      } else {
        await exportUtMatrixRptPpt(rptNm, orderedTargets, visibleElec, visibleMech);
      }
    } catch (e) {
      openMessageBar({ type: 'error', content: `보고서 발행 오류: ${errorMessage(e)}` });
    } finally {
      setPublishing(false);
    }
  };

  /**
   * 항목 버튼 줄 — 목록은 **공통코드가 유일한 출처**이고, 켜짐/꺼짐 상태만 저장된 행에서 읽는다.
   *
   * 공통코드가 없으면 버튼도 없다. 저장된 데이터로 목록을 대신 만들면 리포트마다 항목이
   * 달라 보여서, 코드가 비면 그대로 비우는 쪽이 맞다.
   *
   * 이 리포트에 SUM_M 행이 없는 코드는 버튼을 만들지 않는다 — 토글은 기존 행의
   * RPOT_TGT_YN을 UPDATE하는 동작이라 보여줄 내용도, 바꿀 대상도 없기 때문이다.
   * 반대로 꺼둔(N) 항목은 행이 있으므로 남겨둔다. 그래야 다시 켤 수 있다.
   * 남는 항목이 하나도 없으면 줄 자체를 그리지 않는다.
   */
  const renderFilterRow = (
    label: string,
    codes: UtItemCode[],
    all: RptSumItem[],
    atcDivsCd: AtcDivsCdSum
  ) => {
    const isMech = atcDivsCd === 'MECH';
    const entries = codes
      .map((c) => ({
        cd: c.cd,
        nm: withUnit(c.nm, c.unit, isMech),
        item: all.find((i) => isSameUtItemCode(c.cd, i.utmTpCd, isMech)),
      }))
      .filter((e): e is { cd: string; nm: string; item: RptSumItem } => !!e.item);

    if (entries.length === 0) return null;

    return (
      <div className="filter-row">
        <span className="filter-label">{label} :</span>
        <button
          type="button"
          className="filter-btn"
          onClick={() => showAll(atcDivsCd)}
          disabled={!canSave}
        >
          전체
        </button>
        {entries.map((e) => (
          <button
            type="button"
            key={e.cd}
            className={`filter-btn${e.item.rpotTgtYn === 'Y' ? ' active' : ''}`}
            onClick={() => toggleItem(e.item)}
            disabled={!canSave}
          >
            {e.nm}
          </button>
        ))}
      </div>
    );
  };

  return (
    <AnalysisReportRoot>
      <div className="report-panel" ref={contentRef}>
        <div className="section-title" style={{ marginTop: 0 }}>
          대상 UT Matrix
        </div>
        <div style={{ marginBottom: 16 }}>
          {orderedTargets.length === 0 && !loading ? (
            <span style={{ fontSize: 12, color: '#979998' }}>-</span>
          ) : (
            ['MST', 'TGT', 'REF'].map((role) => {
              const hit = targets.find((x) => x.role === role);
              if (!hit) return null;
              return (
                <span key={role} className={`target-badge role-${role.toLowerCase()}`}>
                  <span className="role-tag">{ROLE_LABEL[role as MstTgtTpCd]}</span>{' '}
                  {CommonUtil.unescapeHtml(hit.utmNm)}({hit.verNo})
                </span>
              );
            })
          )}
        </div>

        {loading && <div className="loading">불러오는 중…</div>}

        {/*
          SUM_M 조회 API가 아직 없어서 실패하는 경우가 정상 경로다.
          오류 문구만 던지지 말고 왜 비어 있는지 함께 알려준다.
        */}
        {!loading && error && (
          <>
            <div className="error-box">{error}</div>
            <div className="notice">
              UT Matrix 용량 정합화 데이터를 불러오지 못했습니다. 리포트 저장 시점에
              TB_EELMB_UTM_RPT_SUM_M이 생성되어 있어야 하며, 조회 API가 아직 준비되지 않았다면 BE
              연동 후 표시됩니다.
            </div>
          </>
        )}

        {!loading && !error && items && items.length === 0 && (
          <div className="notice">이 리포트에는 저장된 UT Matrix 용량 정합화 내역이 없습니다.</div>
        )}

        {!loading && !error && items && items.length > 0 && (
          <>
            <div className="section-title-row">
              <div className="section-title">UT Matrix 용량 정합화</div>
              <div className="section-actions">
                <div className="report-format-select">
                  <label>
                    <input
                      type="radio"
                      name="reportFormat"
                      value="PPT"
                      checked={format === 'PPT'}
                      onChange={() => setFormat('PPT')}
                    />
                    PPT
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="reportFormat"
                      value="PDF"
                      checked={format === 'PDF'}
                      onChange={() => setFormat('PDF')}
                    />
                    PDF
                  </label>
                </div>
                <button
                  type="button"
                  className="btn-mode"
                  onClick={handlePublish}
                  disabled={publishing || reviewItems.length === 0}
                >
                  {publishing ? '발행 중…' : '보고서 발행'}
                </button>
                {canSave && (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={saveAllReviews}
                    disabled={savingAll || reviewItems.length === 0}
                  >
                    {savingAll ? '저장 중…' : '저장'}
                  </button>
                )}
              </div>
            </div>

            {renderFilterRow('전기', itemCodes.elec, elecItems, 'ELEC')}
            {renderFilterRow('기계', itemCodes.mech, mechItems, 'MECH')}

            <div className="sum-table">
              <table>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ width: 90 }}>
                      구분
                    </th>
                    <th rowSpan={2}>UT Matrix</th>
                    <th rowSpan={2} style={{ width: 90 }}>
                      라인
                    </th>
                    {visibleElec.length > 0 && <th colSpan={visibleElec.length}>전기(MW)</th>}
                    {visibleMech.length > 0 && <th colSpan={visibleMech.length}>기계</th>}
                  </tr>
                  <tr>
                    {[...visibleElec, ...visibleMech].map((i) => (
                      <th key={draftKey(i)}>
                        {withUnit(itemLabel(i), itemUnit(i), i.atcDivsCd === 'MECH')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orderedTargets.map((target) => (
                    <tr key={target.utmId}>
                      <td className="center">{ROLE_LABEL[target.role]}</td>
                      <td>
                        {CommonUtil.unescapeHtml(target.utmNm)}({target.verNo})
                      </td>
                      <td className="center">{target.prdnLnNm || '-'}</td>
                      {[...visibleElec, ...visibleMech].map((i) => (
                        <td key={draftKey(i)} className="num">
                          {fmt(i.values[target.role])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="diff-row">
                    <td colSpan={3}>차이(기준 - 비교대상)</td>
                    {[...visibleElec, ...visibleMech].map((i) => (
                      <td
                        key={draftKey(i)}
                        className={`num${(rptSumDiff(i) ?? 0) < 0 ? ' neg' : ''}`}
                      >
                        {fmt(rptSumDiff(i))}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="section-title">UT 사용량 정합화 검토 내역</div>
            <div className="sum-table review-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>전기/기계</th>
                    <th style={{ width: 120 }}>UT 항목</th>
                    <th style={{ width: 90 }}>단위</th>
                    <th style={{ width: 100 }}>UT 부족량</th>
                    <th style={{ width: 200 }}>투자비 영향</th>
                    <th>내역</th>
                    <th style={{ width: 280 }}>향후 계획</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewItems.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="empty">
                        표시할 UT 항목이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    reviewItems.map((item) => {
                      const draft = drafts[draftKey(item)] ?? EMPTY_DRAFT;
                      return (
                        <tr key={draftKey(item)}>
                          <td className="center">{item.atcDivsCd === 'ELEC' ? '전기' : '기계'}</td>
                          <td>{itemLabel(item)}</td>
                          <td className="center">{itemUnit(item) || '-'}</td>
                          <td className={`num${(rptSumDiff(item) ?? 0) < 0 ? ' neg' : ''}`}>
                            {fmt(rptSumDiff(item))}
                          </td>
                          <td>
                            {/* 세 칸 모두 같은 높이로 맞춘다 — 투자비 영향도 여러 줄 입력 */}
                            <textarea
                              className="rvw-textarea"
                              rows={5}
                              value={CommonUtil.safeHtmlContents(draft.infcRvwCtn, false)}
                              disabled={!canSave}
                              onChange={(e) =>
                                setDraftField(
                                  item,
                                  'infcRvwCtn',
                                  CommonUtil.safeHtmlContents(e.target.value, false)
                                )
                              }
                            />
                          </td>
                          <td>
                            <textarea
                              className="rvw-textarea"
                              rows={5}
                              value={CommonUtil.safeHtmlContents(draft.rvwCtn, false)}
                              disabled={!canSave}
                              onChange={(e) =>
                                setDraftField(
                                  item,
                                  'rvwCtn',
                                  CommonUtil.safeHtmlContents(e.target.value, false)
                                )
                              }
                            />
                          </td>
                          <td>
                            <textarea
                              className="rvw-textarea"
                              rows={5}
                              value={CommonUtil.safeHtmlContents(draft.nextPlanCtn, false)}
                              disabled={!canSave}
                              onChange={(e) =>
                                setDraftField(
                                  item,
                                  'nextPlanCtn',
                                  CommonUtil.safeHtmlContents(e.target.value, false)
                                )
                              }
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AnalysisReportRoot>
  );
};

export default AnalysisReportPanel;
